import React, { useContext, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import LoadingScreen from "../../../components/loading-screen";
import {
  BankAccount,
  BankAndTax as BankAndTaxType,
  InventureUser,
  TaxInformation,
} from "../../../types/inventure-user_types";
import API_CLIENT from "../../../utility/api-client";
import { AppContext } from "../direct-router";
import { Page, Text, Grid, Input, Spacer, Divider, Button, Note, useToasts } from "@geist-ui/core";
import { CreditCard, Shield, Mail } from "@geist-ui/icons";
import { useMediaQuery } from "@mui/material";

const DataFieldLabel = ({ label }: { label: string }) => (
    <Text span style={{ 
        fontSize: '11px', 
        fontWeight: 800, 
        color: '#A0A0A0', 
        textTransform: 'uppercase', 
        letterSpacing: '0.08em', 
        display: 'block', 
        marginBottom: '8px',
        fontFamily: '"Nunito Sans", sans-serif' 
    }}>
        {label}
    </Text>
);

const PassiveField = ({ label, value }: { label: string, value: any }) => (
    <div style={{ marginBottom: '24px', width: '100%' }}>
        <DataFieldLabel label={label} />
        <div style={{ 
            background: '#F9F9F9', 
            border: '1px solid #F0F0F0', 
            borderRadius: '12px', 
            padding: '12px 16px', 
            fontSize: '15px', 
            fontWeight: 600, 
            color: '#888',
            fontFamily: '"Nunito Sans", sans-serif',
            minHeight: '48px',
            display: 'flex',
            alignItems: 'center'
        }}>
            {value || '-'}
        </div>
    </div>
);

const ActiveInputField = ({ label, field, placeholder, rules }: any) => (
    <div style={{ marginBottom: '24px', width: '100%' }}>
        <DataFieldLabel label={label} />
        <input 
            {...field}
            placeholder={placeholder}
            style={{ 
                width: '100%',
                background: '#FFFFFF', 
                border: '1px solid #EAEAEA', 
                borderRadius: '12px', 
                padding: '12px 16px', 
                fontSize: '15px', 
                fontWeight: 600, 
                color: '#000',
                fontFamily: '"Nunito Sans", sans-serif',
                minHeight: '48px',
                outline: 'none',
                transition: 'all 0.2s ease',
                boxShadow: 'none'
            }}
            onFocus={(e) => {
                e.target.style.borderColor = '#000';
                e.target.style.boxShadow = '0 0 0 1px #000';
            }}
            onBlur={(e) => {
                e.target.style.borderColor = '#EAEAEA';
                e.target.style.boxShadow = 'none';
            }}
        />
    </div>
);

export default function BankAndTax() {
  const app_context = useContext(AppContext);
  const tax_data = app_context.user?.taxInformation;
  const bank_data = app_context.user?.bank_account;
  const isMobile = useMediaQuery('(max-width:900px)');

  return (
    <div style={{ width: '100%', minHeight: '100vh', background: '#fff', paddingTop: isMobile ? '100px' : '160px', paddingBottom: '100px' }}>
      <div style={{ maxWidth: '1000pt', margin: '0 auto', padding: isMobile ? '0 16px' : '0 24px' }}>
        {/* Header */}
        <div style={{ marginBottom: isMobile ? '40px' : '80px' }}>
             <Text h2 style={{ 
                 margin: 0, 
                 fontWeight: 900, 
                 letterSpacing: '-0.04em', 
                 lineHeight: 1, 
                 fontSize: '48px', 
                 fontFamily: '"Nunito Sans", sans-serif', 
                 color: '#000' 
             }}>
                Bank & Steuern
             </Text>
             <Text p style={{ color: '#888', marginTop: '16px', fontSize: '18px', fontWeight: 500, fontFamily: '"Nunito Sans", sans-serif' }}>
                Verwalten Sie Ihre Auszahlungskonten und Steuerdaten.
             </Text>
        </div>

        <Grid.Container gap={isMobile ? 2 : 4}>
           {/* Section: Auszahlungskonto */}
           <Grid xs={24} md={10}>
             <div style={{ paddingRight: isMobile ? 0 : '40px' }}>
               <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '24px' }}>
                 <div style={{ background: '#F5F5F5', width: '40px', height: '40px', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <CreditCard size={20} color="#000" />
                 </div>
                 <Text h3 style={{ margin: 0, fontWeight: 800, fontSize: '24px', fontFamily: '"Nunito Sans", sans-serif', letterSpacing: '-0.02em' }}>Bankkonto</Text>
               </div>
               <Text p style={{ color: '#666', fontSize: '15px', lineHeight: '1.6', fontFamily: '"Nunito Sans", sans-serif' }}>
                 Geben Sie hier das Bankkonto an, auf das wir Ihre Erlöse und Rückzahlungen überweisen sollen.
               </Text>
             </div>
           </Grid>
           <Grid xs={24} md={14}>
             <div style={{ width: '100%' }}>
               {app_context.user?.natural_person ? (
                 <BankForm bank_data={bank_data} />
               ) : (
                 <div style={{ background: '#FFFBEB', border: '1px solid #FEF3C7', padding: '24px', borderRadius: '16px', color: '#92400E', fontSize: '15px', fontWeight: 500, fontFamily: '"Nunito Sans", sans-serif' }}>
                    Sobald Sie Ihre persönlichen Daten angegeben haben, können Sie hier Ihr Bankkonto hinterlegen.
                 </div>
               )}
             </div>
           </Grid>

           <Grid xs={24}><div style={{ width: '100%', height: '1px', background: '#F0F0F0', margin: '40px 0' }} /></Grid>

           {/* Section: Steuern */}
           <Grid xs={24} md={10}>
             <div style={{ paddingRight: isMobile ? 0 : '40px' }}>
               <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '24px' }}>
                 <div style={{ background: '#F5F5F5', width: '40px', height: '40px', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Shield size={20} color="#000" />
                 </div>
                 <Text h3 style={{ margin: 0, fontWeight: 800, fontSize: '24px', fontFamily: '"Nunito Sans", sans-serif', letterSpacing: '-0.02em' }}>Steuerdaten</Text>
               </div>
               <Text p style={{ color: '#666', fontSize: '15px', lineHeight: '1.6', fontFamily: '"Nunito Sans", sans-serif' }}>
                 Diese Angaben werden für die ordnungsgemäße Abführung von Kapitalertragsteuern benötigt.
               </Text>
             </div>
           </Grid>
           <Grid xs={24} md={14}>
             <div style={{ width: '100%' }}>
               <TaxForm tax_data={tax_data} />
             </div>
           </Grid>
        </Grid.Container>
      </div>
    </div>
  );
}

function TaxForm(props: { tax_data?: TaxInformation }) {
  const app_context = useContext(AppContext);
  const { setToast } = useToasts();
  const { control, formState, handleSubmit, reset } = useForm({ mode: "onChange" });
  const [is_loading, setIsLoading] = useState(false);

  const submit = (data: any) => {
    setIsLoading(true);
    const tax_data = {
      tax_identification_number: data.identification_number,
      tax_number: "",
      tax_office: data.tax_office,
    } as TaxInformation;
    API_CLIENT.putTax(
      app_context.token,
      tax_data,
      (user) => {
        app_context.updateUser(user);
        reset({
          identification_number: tax_data.tax_identification_number,
          tax_office: tax_data.tax_office
        });
        setIsLoading(false);
        setToast({ type: 'success', text: 'Steuerdaten erfolgreich aktualisiert.' });
      },
      (error) => {
        setIsLoading(false);
        setToast({ type: 'error', text: 'Fehler beim Aktualisieren der Steuerdaten.' });
      }
    );
  };

  return (
    <form onSubmit={handleSubmit(submit)} style={{ display: 'flex', flexDirection: 'column' }}>
      <Controller
        name="identification_number"
        control={control}
        defaultValue={props.tax_data?.tax_identification_number || ""}
        rules={{ required: true }}
        render={({ field }) => (
          <ActiveInputField label="Steueridentifikationsnummer" field={field} placeholder="Z.B. 12 345 678 901" />
        )}
      />

      <Controller
        name="tax_office"
        control={control}
        defaultValue={props.tax_data?.tax_office || ""}
        rules={{ required: true }}
        render={({ field }) => (
          <ActiveInputField label="Wohnsitzfinanzamt" field={field} placeholder="Z.B. Berlin Mitte" />
        )}
      />

      <Button
        auto
        htmlType="submit"
        loading={is_loading}
        disabled={!formState.isDirty || !formState.isValid}
        style={{ 
            marginTop: '16px',
            height: '48px',
            padding: '0 32px',
            background: '#000', 
            color: '#fff', 
            borderRadius: '100px',
            fontSize: '15px',
            fontWeight: 800,
            border: 'none',
            fontFamily: '"Nunito Sans", sans-serif',
            alignSelf: 'flex-start'
        }}
      >
        Aktualisieren
      </Button>
    </form>
  );
}

function BankForm(props: { bank_data?: BankAccount }) {
  const app_context = useContext(AppContext);
  const { setToast } = useToasts();
  const { control, reset, formState, handleSubmit } = useForm({ mode: "onChange" });
  const [is_loading, setIsLoading] = useState(false);

  const onSubmit = (data: any) => {
    setIsLoading(true);
    const bank = {
      bic: data.bic,
      iban: data.iban,
      bank: data.bank,
      account_holder:
        app_context.user?.natural_person.forename +
        " " +
        app_context.user?.natural_person.surname,
    } as BankAccount;
    API_CLIENT.putBankAccount(
      app_context.token,
      bank,
      (user: InventureUser) => {
        app_context.updateUser(user);
        reset({ bank: bank.bank, iban: bank.iban, bic: bank.bic });
        setIsLoading(false);
        setToast({ type: 'success', text: 'Bankverbindung erfolgreich aktualisiert.' });
      },
      (error) => {
        setIsLoading(false);
        setToast({ type: 'error', text: 'Fehler beim Aktualisieren der Bankverbindung.' });
      }
    );
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} style={{ display: 'flex', flexDirection: 'column' }}>
      <Controller
        name="bank"
        control={control}
        defaultValue={props.bank_data?.bank || ""}
        rules={{ required: true }}
        render={({ field }) => (
          <ActiveInputField label="Name der Bank" field={field} placeholder="Z.B. Deutsche Bank" />
        )}
      />

      <PassiveField 
        label="Kontoinhaber" 
        value={(app_context.user?.natural_person.forename + " " + app_context.user?.natural_person.surname) || ""} 
      />

      <Controller
        name="iban"
        control={control}
        defaultValue={props.bank_data?.iban || ""}
        rules={{ required: true }}
        render={({ field }) => (
          <ActiveInputField label="IBAN" field={field} placeholder="DE00 0000 0000 0000 0000 00" />
        )}
      />

      <Controller
        name="bic"
        control={control}
        defaultValue={props.bank_data?.bic || ""}
        rules={{ required: true }}
        render={({ field }) => (
          <ActiveInputField label="BIC" field={field} placeholder="ABCDEFGH" />
        )}
      />

      <Button
        auto
        htmlType="submit"
        loading={is_loading}
        disabled={!formState.isDirty || !formState.isValid}
        style={{ 
            marginTop: '16px',
            height: '48px',
            padding: '0 32px',
            background: '#000', 
            color: '#fff', 
            borderRadius: '100px',
            fontSize: '15px',
            fontWeight: 800,
            border: 'none',
            fontFamily: '"Nunito Sans", sans-serif',
            alignSelf: 'flex-start'
        }}
      >
        Aktualisieren
      </Button>
    </form>
  );
}

